<template>
  <div class="container mt-4" dir="rtl">
    
    <!-- 1. لوحة التحكم والبحث -->
    <div class="card shadow-sm mb-4 border-top-primary no-print">
      <div class="card-header bg-white py-3">
        <h5 class="mb-0 text-primary fw-bold">
          <i class="fas fa-edit ms-2"></i> رصد الدرجات (نظام التحقق الذكي)
        </h5>
      </div>

      <div class="card-body">
        <div class="row g-3">
          <!-- الفصل الدراسي -->
          <div class="col-md-4">
            <label class="form-label fw-bold small">الفصل الدراسي</label>
            <select v-model="form.term" @change="getSubjectswithtearm(form.term)" class="form-select">
              <option value="">-- اختر الفصل --</option>
              <option v-for="t in terms" :key="t.id" :value="t">{{ t.name }}</option>
            </select>
          </div>

          <!-- المادة -->
          <div class="col-md-4">
            <label class="form-label fw-bold small">المادة</label>
            <select v-model="form.subject" class="form-select">
              <option value="">-- اختر المادة --</option>
              <option v-for="s in subjects" :key="s.id" :value="s">{{ s.name }}</option>
            </select>
          </div>

          <!-- المجموعة -->
          <div class="col-md-4">
            <label class="form-label fw-bold small">المجموعة</label>
            <select @change="getResults(1)" v-model="form.group" class="form-select">
              <option value="">-- اختر المجموعة --</option>
              <option v-for="g in groups" :key="g.id" :value="g.id">{{ g.group_name }}</option>
            </select>
          </div>
        </div>
      </div>
    </div>

    <!-- 2. جدول الرصد -->
    <div v-if="collection.length > 0 && form.subject" class="card shadow-sm border-0">
      
      <!-- ترويسة الجدول -->
      <div class="card-header bg-light d-flex justify-content-between align-items-center flex-wrap gap-2">
        <div class="d-flex align-items-center gap-3">
           <h6 class="fw-bold mb-0 text-dark">
             <i class="fas fa-book-open ms-1 text-secondary"></i> {{ form.subject.name }}
           </h6>
           <div class="d-flex gap-2">
             <span class="badge bg-info text-dark border">العظمى: {{ form.subject.max }}</span>
             <span class="badge bg-warning text-dark border">الصغرى: {{ form.subject.main }}</span>
           </div>
        </div>
        <!-- زر الحفظ الجماعي -->
        <button class="btn btn-success btn-sm px-4 shadow-sm" @click="SaveData" :disabled="isSaving">
           <span v-if="isSaving" class="spinner-border spinner-border-sm me-1"></span>
           <i class="fas fa-save ms-1"></i> حفظ الكل
        </button>
      </div>

      <div class="card-body p-0">
        <div class="table-responsive" style="max-height: 70vh; overflow-y: auto;">
          <table class="table table-bordered table-hover text-center align-middle mb-0 custom-table sticky-header">
            <thead class="bg-dark text-white">
              <tr>
                <th style="width: 5%">م</th>
                <th style="width: 15%">رقم السري (النظام)</th>
                <th style="width: 20%" class="bg-warning text-dark border-warning">
                  <i class="fas fa-key ms-1"></i> تأكيد (آخر 3 أرقام)
                </th>
                <th style="width: 20%">الدرجة</th>
                <th style="width: 40%">الحالة والملاحظات</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="(student, index) in collection" :key="student.id" :class="getRowClass(student)">
                
                <td>{{ index + 1 }}</td>

                <!-- 1. الرقم السري (للقراءة فقط) -->
                <td class="bg-light fs-5 user-select-none">
                  <span class="text-muted opacity-50 font-monospace small">
                    {{ getPrefix(student.SecretKey) }}
                  </span>
                  <span class="fw-bold text-dark font-monospace border-bottom border-dark border-2 px-1">
                    {{ getLast3(student.SecretKey) }}
                  </span>
                </td>
                
                <!-- 2. خانة التحقق (الإدخال الأول) -->
                <td class="p-2 bg-warning-subtle position-relative">
                  <input 
                    :id="'verify-' + index"
                    v-model="student.input_secret"
                    type="number" 
                    class="form-control text-center fw-bold shadow-sm"
                    :class="getVerifyClass(student)"
                    placeholder="اكتب 3 أرقام"
                    @keyup.enter="checkSecretMatch(student, index)"
                    @blur="checkSecretMatch(student, index)"
                    autocomplete="off"
                  >
                </td>

                <!-- 3. خانة الدرجة -->
                <td class="p-2 position-relative">
                  <div class="input-group">
                    
                    <!-- الحالة أ: حقل إدخال الدرجة (للطلاب العاديين) -->
                    <input
                      v-if="!isSpecialStatus(student)"
                      :id="'degree-' + index"
                      v-model.number="student[form.subject.col_name] = student[form.subject.code]" 
                      type="number"
                      class="form-control text-center fw-bold fs-4 degree-input"
                      :disabled="!student.is_verified"
                      :class="getInputClass(student)"
                      
                      @input="validateDegree(student)"
                      @keyup.enter="saveAndMove(student, index)" 
                      @blur="saveSingleRow(student)" 
                      
                      min="0"
                      :max="form.subject.max"
                    />
                    
                    <!-- الحالة ب: حقل للقراءة فقط (للحالات الخاصة: غياب/لغات/دمج) -->
                    <input
                      v-else
                      type="text"
                      class="form-control text-center fw-bold fs-5 text-white"
                      :class="getInputClass(student)"
                      :value="getInputText(student)"
                      disabled
                    />

                  </div>

                  <!-- أيقونة حالة الحفظ (تظهر يسار الحقل) -->
                  <div class="position-absolute top-50 end-0 translate-middle-y me-3">
                      <span v-if="student.saving" class="spinner-border spinner-border-sm text-primary"></span>
                      <i v-else-if="student.saved" class="fas fa-check-circle text-success fs-5"></i>
                      <i v-else-if="student.save_error" class="fas fa-times-circle text-danger fs-5" title="فشل الحفظ"></i>
                  </div>
                </td>

                <!-- 4. الملاحظات -->
                <td class="text-start px-3 small fw-bold">
                  <!-- خطأ التحقق -->
                  <div v-if="student.verify_error" class="text-danger mb-1 animate-pulse">
                     <i class="fas fa-times-circle ms-1"></i> الرقم لا يطابق!
                  </div>
                  
                  <!-- خطأ تجاوز الدرجة -->
                  <div v-if="hasError(student)" class="text-danger fw-bold">
                    <i class="fas fa-hand-paper ms-1"></i> خطأ: الدرجة أكبر من العظمى ({{ form.subject.max }})
                  </div>

                  <!-- الحالات الخاصة -->
                  <div v-else-if="getSpecialStatusText(student)" class="text-muted">
                    <span class="badge" :class="getBadgeClass(student)">
                        <i class="fas fa-info-circle ms-1"></i> {{ getSpecialStatusText(student) }}
                    </span>
                  </div>
                  
                  <!-- حالة الرسوب -->
                  <div v-else-if="isFailed(student)" class="text-warning text-dark">
                    <i class="fas fa-arrow-down ms-1"></i> راسب
                  </div>

                  <!-- تم الرصد -->
                  <div v-else-if="student.is_verified && student[form.subject.col_name] !== null" class="text-success">
                    <i class="fas fa-check-double ms-1"></i> تم الرصد
                  </div>
                </td>

              </tr>
            </tbody>
          </table>
        </div>
      </div>

      <!-- التصفح -->
      <div class="card-footer bg-white py-3 d-flex justify-content-center">
         <div class="btn-group" v-if="totalPages > 1">
            <button class="btn btn-outline-secondary btn-sm" @click="loadPage(currentPage - 1)" :disabled="currentPage === 1">السابق</button>
            <button class="btn btn-outline-secondary btn-sm disabled">{{ currentPage }} / {{ totalPages }}</button>
            <button class="btn btn-outline-secondary btn-sm" @click="loadPage(currentPage + 1)" :disabled="currentPage === totalPages">التالي</button>
         </div>
      </div>

    </div>

    <!-- رسالة فارغة -->
    <div v-else-if="searched && collection.length === 0" class="alert alert-info text-center mt-4 shadow-sm">
      <i class="fas fa-info-circle fa-2x mb-3 d-block opacity-50"></i>
      لا توجد بيانات للعرض.
    </div>

  </div>
</template>

<script>
import api from '@/api/axios';
import Swal from 'sweetalert2'; 

export default {
  name: "GradeMonitoring",
  data() {
    return {
      currentPage: 1,
      totalPages: 1,
      isSaving: false,
      searched: false,
      terms: [], subjects: [], groups: [], collection: [],
      form: { term: "", subject: "", group: "" },
    };
  },
  mounted() {
    this.fetchInitialData();
  },
  methods: {
    // --- (أ) دوال الحفظ الأساسية ---

    // 1. الحفظ والانتقال (عند ضغط Enter)
    async saveAndMove(student, index) {
        // التحقق من الخطأ قبل محاولة الحفظ
        if (this.hasError(student)) {
            // إظهار تنبيه وعدم الحفظ وعدم الانتقال
            Swal.fire({
              toast: true,
              position: 'top-start',
              icon: 'error',
              title: 'الدرجة أكبر من النهاية العظمى!',
              showConfirmButton: false,
              timer: 1500
            });
            return; 
        }

        // الحفظ
        await this.saveSingleRow(student);
        
        // الانتقال
        this.focusNextVerify(index);
    },

    // 2. دالة حفظ صف واحد (Backend Call)
    async saveSingleRow(student) {
        // شروط المنع:
        // 1. لم يتم التحقق من السري
        // 2. الطالب حالة خاصة (غياب/لغات...)
        // 3. الدرجة أكبر من العظمى (Double Check)
        if (!student.is_verified || this.isSpecialStatus(student) || this.hasError(student)) return;

        // تفعيل المؤشرات
        this.$set(student, 'saving', true);
        this.$set(student, 'saved', false);
        this.$set(student, 'save_error', false);

        try {
            await api.post('/save-single-degree', {
                id: student.id,
                col_name: this.form.subject.code,
                degree: student[this.form.subject.col_name]
            });

            this.$set(student, 'saving', false);
            this.$set(student, 'saved', true);
            setTimeout(() => { this.$set(student, 'saved', false); }, 2000);

        } catch (error) {
            console.error(error);
            this.$set(student, 'saving', false);
            this.$set(student, 'save_error', true);
        }
    },

    // 3. الحفظ الجماعي (الزر العلوي)
    async SaveData() {
      // فحص هل يوجد أي خطأ في القائمة بالكامل؟
      const hasErrors = this.collection.some(s => this.hasError(s));
      if (hasErrors) {
          Swal.fire('خطأ', 'توجد درجات تتجاوز النهاية العظمى (باللون الأحمر). يرجى تصحيحها قبل الحفظ.', 'error');
          return; // إيقاف العملية
      }

      this.isSaving = true;
      try {
        await api.post("/save-data-degree", {
          students: this.collection,
          subject_id: this.form.subject.id,
          col_name: this.form.subject.col_name
        });

        Swal.fire({
            icon: 'success',
            title: 'تم حفظ جميع الدرجات',
            showConfirmButton: false,
            timer: 1500
        });

      } catch (e) {
        Swal.fire('خطأ', 'حدثت مشكلة أثناء الحفظ', 'error');
      } finally {
        this.isSaving = false;
      }
    },

    // --- (ب) دوال التحقق والمنطق ---

    // هل الطالب حالة خاصة؟
    isSpecialStatus(student) {
        const degree = parseInt(student[this.form.subject.code]);
        // الأكواد: -1 (غياب), -2 (لغات), -3 (دمج), -4 (خاصة)
        return [-1, -2, -3, -4].includes(degree);
    },

    // هل يوجد خطأ (أكبر من العظمى)؟
    hasError(student) {
        if (this.isSpecialStatus(student)) return false; // الحالات الخاصة معفية من الفحص
        
        const degree = student[this.form.subject.col_name];
        // إذا الحقل فارغ لا يعتبر خطأ (مؤقتاً)
        if (degree === '' || degree === null) return false;

        return degree > this.form.subject.max;
    },

    // هل راسب؟
    isFailed(student) {
        if (this.isSpecialStatus(student)) return true; // يعتبر راسب مؤقتاً
        const degree = student[this.form.subject.col_name];
        return degree < this.form.subject.main;
    },

    // منع القيم السالبة أثناء الكتابة
    validateDegree(student) {
        const colName = this.form.subject.col_name;
        let val = student[colName];
        // نمنع السالب لأن الحالات الخاصة تأتي جاهزة من الداتابيز
        if (val < 0) student[colName] = 0;
    },

    // --- (ج) دوال الجلب والتهيئة ---
    async fetchInitialData() {
      try {
        const [termsRes, groupsRes] = await Promise.all([
          api.get("/getterms"),
          api.get("/getgroups")
        ]);
        this.terms = termsRes.data;
        this.groups = groupsRes.data;
      } catch (e) { console.error(e); }
    },

    async getSubjectswithtearm(term) {
      if (!term) return;
      try {
        const res = await api.get("/getSubject/" + term.id);
        this.subjects = res.data; 
      } catch (e) { console.error(e); }
    },

    async getResults(page = 1) {
      if (!this.form.term || !this.form.subject || !this.form.group) return;
      
      this.searched = true;
      this.currentPage = page;

      try {
        const res = await api.get("/getstudentInStored", {
          params: { page, term: this.form.term, subject: this.form.subject, group: this.form.group },
        });
        
        // تهيئة البيانات
        this.collection = res.data.data.map(std => ({
            ...std,
            input_secret: '',
            is_verified: false,
            verify_error: false,
            saving: false,
            saved: false,
            save_error: false
        }));
        
        this.totalPages = res.data.last_page;
        this.$nextTick(() => {
            const firstInput = document.getElementById('verify-0');
            if(firstInput) firstInput.focus();
        });

      } catch (e) { console.error(e); }
    },

    loadPage(pageNum) {
        if(pageNum < 1 || pageNum > this.totalPages) return;
        this.getResults(pageNum);
    },

    // --- (د) التحقق الذكي من الرقم السري ---
    checkSecretMatch(student, index) {
      if (!student.input_secret && student.input_secret !== 0) return;

      const inputNum = parseInt(student.input_secret);
      const realSecretNum = parseInt(student.SecretKey);
      const realSecretStr = String(student.SecretKey);
      const last3Digits = parseInt(realSecretStr.slice(-3));

      // نقبل الرقم كاملاً أو آخر 3 أرقام
      if (inputNum === realSecretNum || inputNum === last3Digits) {
        student.is_verified = true;
        student.verify_error = false;
        this.$forceUpdate();
        
        // إظهار الرقم الكامل
        student.input_secret = student.SecretKey;

        this.$nextTick(() => {
          const degreeInput = document.getElementById('degree-' + index);
          if (degreeInput && !degreeInput.disabled) {
              degreeInput.focus();
              degreeInput.select();
          } else {
              // تخطي الحالات الخاصة
              this.focusNextVerify(index);
          }
        });
      } else {
        student.is_verified = false;
        student.verify_error = true;
        this.$forceUpdate();
      }
    },

    focusNextVerify(currentIndex) {
      const nextIndex = currentIndex + 1;
      const nextVerifyInput = document.getElementById('verify-' + nextIndex);
      if (nextVerifyInput) {
        nextVerifyInput.focus();
      } else {
        Swal.fire({
            title: 'انتهت القائمة',
            text: 'هل تريد الانتقال للصفحة التالية؟',
            showCancelButton: true,
            confirmButtonText: 'نعم',
            cancelButtonText: 'لا'
        }).then((res) => {
            if(res.isConfirmed && this.currentPage < this.totalPages) {
                this.loadPage(this.currentPage + 1);
            }
        });
      }
    },

    // --- (هـ) دوال التنسيق البصري ---
    
    // النصوص والألوان للحالات الخاصة
    getInputText(student) {
        const degree = parseInt(student[this.form.subject.code]);
        switch (degree) {
            case -1: return 'غـائـب';
            case -2: return 'لـغـات';
            case -3: return 'دمــج';
            case -4: return 'خـاصـة';
            default: return '';
        }
    },
    getSpecialStatusText(student) {
        const degree = parseInt(student[this.form.subject.code]);
        switch (degree) {
            case -1: return 'مسجل غياب';
            case -2: return 'طالب لغات';
            case -3: return 'طالب دمج';
            case -4: return 'لجنة خاصة';
            default: return null;
        }
    },
    getInputClass(student) {
        const degree = parseInt(student[this.form.subject.code]);
        // ألوان الحالات الخاصة
        if (degree === -1) return 'bg-secondary text-white';
        if (degree === -2) return 'bg-info text-white';
        if (degree === -3) return 'bg-warning text-dark';
        if (degree === -4) return 'bg-dark text-white';

        // ألوان الدرجات العادية
        if (this.hasError(student)) return 'border-danger text-danger bg-danger-subtle';
        if (this.isFailed(student)) return 'text-warning border-warning';
        return 'border-success text-dark';
    },
    getBadgeClass(student) {
        const degree = parseInt(student[this.form.subject.code]);
        if (degree === -1) return 'bg-secondary';
        if (degree === -2) return 'bg-info text-dark';
        if (degree === -3) return 'bg-warning text-dark';
        if (degree === -4) return 'bg-dark';
        return '';
    },
    
    // مساعدات بصرية أخرى
    getPrefix(secret) { return String(secret).slice(0, -3); },
    getLast3(secret) { return String(secret).slice(-3); },
    
    getVerifyClass(student) {
        if (student.verify_error) return 'is-invalid border-danger text-danger bg-danger-subtle';
        if (student.is_verified) return 'is-valid border-success text-success';
        return '';
    },
    getRowClass(student) {
        if (this.isSpecialStatus(student)) return 'table-secondary'; 
        if (student.verify_error) return 'table-danger';
        return '';
    },
  },
};
</script>

<style scoped>
.border-top-primary { border-top: 4px solid #0d6efd; }
.degree-input { color: #0d6efd; font-family: 'Courier New', monospace; letter-spacing: 1px; }
.sticky-header th { position: sticky; top: 0; z-index: 10; box-shadow: 0 2px 2px -1px rgba(0, 0, 0, 0.4); }
.bg-warning-subtle { background-color: #fff8e1; }
.bg-danger-subtle { background-color: #ffebe9 !important; }
.user-select-none { user-select: none; }
</style>